import {keycloakConfig} from './config';
import Keycloak, {KeycloakInitOptions} from 'keycloak-js'
import fromPairs from 'lodash/fromPairs';
import { Cookies } from 'react-cookie';
import { setCookiesFromServer } from './set-cookies-from-server';

// window is unavailable during Next.js SSR of 'use client' modules — this
// only matters for the k_cl override, so just fall back to config then.
const urlParams = typeof window !== 'undefined' ? new URLSearchParams(window.location.search) : null;
const clientId = urlParams?.has('k_cl') ? urlParams.get('k_cl') as string : keycloakConfig.clientId

const AUTH_RECOVERY_KEY = 'kc-refresh-recovery-at';
const AUTH_RECOVERY_COOLDOWN_MS = 60_000;

// Token refresh (updateToken) can fail once the Keycloak session/refresh token
// has actually expired (e.g. tab left open/backgrounded for a while). When that
// happens there's no way to recover in-place, so requests keep going out
// unauthenticated forever. A reload re-runs keycloak.init() and re-authenticates
// from scratch, which is the only thing that actually fixes it. The cooldown
// guards against a reload loop if the session stays unrecoverable.
export function recoverFromAuthFailure() {
  const last = Number(sessionStorage.getItem(AUTH_RECOVERY_KEY) || 0);
  const now = Date.now();
  if (now - last < AUTH_RECOVERY_COOLDOWN_MS) {
    return;
  }
  sessionStorage.setItem(AUTH_RECOVERY_KEY, String(now));
  window.location.reload();
}

function attachTokenRefreshHandlers(instance: Keycloak) {
  instance.onAuthRefreshError = () => {
    console.log('onAuthRefreshError');
    recoverFromAuthFailure();
  };

  instance.onTokenExpired = () => {
    instance.updateToken(10).catch(() => recoverFromAuthFailure());
  };
}

export let keycloak = new Keycloak({ ...keycloakConfig, clientId });
attachTokenRefreshHandlers(keycloak);

async function addTokensToSessionStorageFromMagicLink() {
  const params = new URLSearchParams(document.location.search);

  if (params.has('key')) {
    const c = new Cookies();
    const clientKey = c.get('next-auth.clientKey');
    const username = c.get('next-auth.username');
    const url = `${keycloakConfig.url}realms/${keycloakConfig.realm}/protocol/openid-connect/token`
    try {
      const tokens = await fetch(url, {
        method: 'POST',
        body: new URLSearchParams({
          'grant_type': 'password',
          'client_id': clientId,
          'scope': 'openid',
          'emailKey': params.get('key') || '',
          clientKey,
          username
        }),
      }).then(r => r.json());
      sessionStorage.setItem('token', tokens.access_token);
      sessionStorage.setItem('refreshToken', tokens.refresh_token);
      sessionStorage.setItem('idToken', tokens.id_token);
      sessionStorage.setItem('clif', 'false');

      await setCookiesFromServer(tokens.access_token);
    } catch (e) {
      // magic-link exchange is best-effort — ignore failures
    }
  }
}

export const initKeycloak = async (options: KeycloakInitOptions = {}, keycloakInstance?: Keycloak) => {
  if (keycloakInstance) {
    keycloak = keycloakInstance;
    attachTokenRefreshHandlers(keycloak);
  }
  addTokensToSessionStorageFromUrl();

  await addTokensToSessionStorageFromMagicLink()

  await keycloak.init({
    token: sessionStorage.getItem('token') || undefined,
    refreshToken: sessionStorage.getItem('refreshToken') || undefined,
    idToken: sessionStorage.getItem('idToken') || undefined,
    onLoad: "check-sso",
    checkLoginIframe: sessionStorage.getItem('clif') !== 'false',
    silentCheckSsoRedirectUri: window.location.origin + window.location.pathname,
    ...options
  });

  (window as any).keycloak = keycloak;

  return keycloak;
}

export const initKeycloakIfAuthenticated = async (options: KeycloakInitOptions = {}) => {
  const value = await initKeycloak(options);

  if (value.authenticated) {
    (window as any).keycloak = value;
    return value;
  } else {
    keycloak = new Keycloak(keycloakConfig);
    (window as any).keycloak = keycloak;
    attachTokenRefreshHandlers(keycloak);
    return keycloak;
  }
}

export const initKeycloakAndAuthorize = async (options: KeycloakInitOptions = {}, clientId?: string) => {
  // Own instance: avoids racing the app-startup check-sso init (see keycloak-js "already initialized").
  const instanceConfig = clientId ? { ...keycloakConfig, clientId } : keycloakConfig;
  const value = await initKeycloak({ ...options, checkLoginIframe: false }, new Keycloak(instanceConfig));

  const token = options.token || keycloak.token;

  if (!token) {
    throw new Error('Unable to initialize keycloak token');
  }

  await setCookiesFromServer(token);

  return value;
}

export const getHash = () => {
  if (keycloak.authenticated) {
    return `#at=${keycloak?.token}&rt=${keycloak?.refreshToken}&it=${keycloak?.idToken}`
  }
  return '';
}

export const getIsAdmin = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_ADMIN)
export const getIsCSR = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_CSR)
export const getIsCSRManager = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_CSR_MANAGER)
export const getIsAgent = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_ADMIN) && !keycloak.hasRealmRole(KeycloakRoles.ROLE_CSR) && !keycloak.hasRealmRole(KeycloakRoles.ROLE_AGENT_MANAGER) && !keycloak.hasRealmRole(KeycloakRoles.ROLE_TECH_STAFF) && !keycloak.hasRealmRole(KeycloakRoles.ROLE_SUPER_ADMIN)
export const getIsDeveloper = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_TECH_STAFF)
export const getIsAgentManager = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_AGENT_MANAGER);
export const getIsSuperAdmin = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_SUPER_ADMIN)
export const getIsAdviser = (keycloak: Keycloak) => keycloak.hasRealmRole(KeycloakRoles.ROLE_ADVISER)

// Medigap underwriting splits access two ways (see medigap-underwriting-frontend-integration.md §2):
// anyone in the answer group may read question sets and read/write a client's answers, while only
// the manager group may edit the question catalog itself. Mirrors the backend's @HasAnyRole gates,
// so keep these in step with the directives in the medigap/enrollment schemas.
export const canAnswerUnderwriting = (keycloak: Keycloak) =>
  getIsSuperAdmin(keycloak) || getIsAgentManager(keycloak) || getIsCSRManager(keycloak) || getIsAdviser(keycloak) || getIsDeveloper(keycloak)
export const canManageUnderwriting = (keycloak: Keycloak) =>
  getIsSuperAdmin(keycloak) || getIsAgentManager(keycloak) || getIsDeveloper(keycloak)

// Client SMS (Aircall) is gated to ROLE_ADMIN / ROLE_TECH_STAFF on every operation -
// clientSmsMessages, sendClientSms and dialClient all carry the same @has_any_role_directive.
// TECH_STAFF may read and send, but its reads come back obfuscated (phoneNumber is the literal
// string "hidden" and `agent` is masked), so never assume phoneNumber is a real number.
export const canUseClientSms = (keycloak: Keycloak) =>
  getIsAdmin(keycloak) || getIsDeveloper(keycloak)

const addTokensToSessionStorageFromUrl = () => {
  if (isAuthInHash()) {
    const auth = fromPairs(document.location.hash.slice(1).split('&').map(v => v.split('=')));
    sessionStorage.setItem('token', auth.at);
    sessionStorage.setItem('refreshToken', auth.rt);
    sessionStorage.setItem('idToken', auth.it);

    if (auth.clif) {
      sessionStorage.setItem('clif', auth.clif);
    }

    window.history.replaceState(null, document.title, window.location.pathname + window.location.search);
  }
}

const isAuthInHash = (): boolean => {
  return document.location.hash.indexOf('at=') >= 0 && document.location.hash.indexOf('rt=') >= 0 && document.location.hash.indexOf('it=') >= 0
}


export enum KeycloakRoles {
  ROLE_ADMIN = 'ROLE_ADMIN',
  ROLE_ALFA = 'ROLE_ALFA',
  ROLE_TECH_STAFF = 'ROLE_TECH_STAFF',
  ROLE_CSR = 'ROLE_CSR',
  ROLE_CSR_MANAGER = 'ROLE_CSR_MANAGER',
  ROLE_AGENT_MANAGER = 'ROLE_AGENT_MANAGER',
  ROLE_ADVISER = 'ROLE_ADVISER',
  ROLE_SUPER_ADMIN = 'ROLE_SUPER_ADMIN',
}

export const getToken = () => {
  return new Promise<string | undefined>((resolve, reject) => {
    // An anonymous visitor has no session to refresh, so updateToken()
    // rejecting here is expected, not a recoverable auth failure — resolve
    // undefined instead of letting callers treat it as one (see recoverFromAuthFailure).
    if (!keycloak.authenticated) {
      resolve(undefined);
      return;
    }
    keycloak.updateToken(5)
      .then(() => {
        resolve(keycloak.token);
      })
      .catch((e: any) => {
        reject(e);
      })
  });
}

export { createLoginUrl } from './create-login-url';
